import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { LocaleService } from '@maximizer/core/shared/data-access';
import { getFontAwesomeIcon } from '@maximizer/core/shared/ui/icons';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { startOfDay } from 'date-fns';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  AnswerAction,
  AnswerActions,
} from '../../answer-widgets/answer-widget.model';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../../classes/event-tracker';
import { sameKeys } from '../../classes/value-checks';
import {
  crmCitationTypeKey,
  crmRecordIcon,
} from '../citations/crm-citation.model';
import { formatCitationDate } from '../pipes/crm-citation-ref.pipe';
import { SuggestedActionDialogComponent } from './suggested-action-dialog.component';
import {
  CREATE_EVENT_NAMES,
  CREATE_LABEL_KEYS,
} from './suggested-action.labels';
import {
  AnswerActionWhen,
  resolveWhen,
  SuggestedActionService,
} from './suggested-action.service';
import {
  fenceHash,
  SuggestedActionId,
  SuggestedActionsStore,
  SuggestedActionStatus,
} from './suggested-actions.store';

/** One card: the action as parsed, resolved to a day, with the words its header and button show. */
interface ActionCard {
  id: SuggestedActionId;
  action: AnswerAction;
  when: AnswerActionWhen;
  icon: string;
  typeKey: string;
  createKey: string;
  date: string;
  /** Set before now: shown as written, flagged, for the advisor to change. */
  past: boolean;
}

/**
 * The next steps an answer suggests, as cards the advisor confirms one by one in a dialog, as
 * classic confirms a CRM action; the record then opens to refine. State is in the store.
 */
@Component({
  selector: 'ai-answer-actions',
  templateUrl: './answer-actions.component.html',
  styleUrl: './answer-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    KENDO_BUTTON,
    KENDO_LOADER,
    SuggestedActionDialogComponent,
    TranslatePipe,
  ],
})
export class AnswerActionsComponent {
  private readonly store = inject(SuggestedActionsStore);
  private readonly service = inject(SuggestedActionService);
  private readonly tracker = inject(IQBoostEventTracker);
  private readonly locale = inject(LocaleService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private destroyed = false;

  readonly actions = input.required<AnswerActions>();
  /** The fence as written, which with the thread tells this answer's cards from another's. */
  readonly source = input.required<string>();
  readonly threadId = input<string | null>(null);

  protected readonly createdIcon = getFontAwesomeIcon('circle-check', 'solid');
  protected readonly openIcon = getFontAwesomeIcon(
    'arrow-up-right-from-square',
    'solid',
  );

  private readonly fence = computed(() => fenceHash(this.source()));

  // The clock is read when the cards are drawn, and again when one is confirmed or tried again.
  protected readonly cards = computed<ActionCard[]>(() => {
    const now = new Date();
    const threadId = this.threadId();
    const fence = this.fence();

    return this.actions().items.map((action, i) =>
      this.toCard({ threadId, fence, index: action.index ?? i }, action, now),
    );
  });

  // The entries the cards are for, named once per answer.
  private readonly entryKeys = computed(
    () => [
      ...new Set(
        this.actions().items.flatMap((item) =>
          item.withEntityKey ? [item.withEntityKey] : [],
        ),
      ),
    ],
    { equal: sameKeys },
  );

  protected readonly names = toSignal(
    toObservable(this.entryKeys).pipe(switchMap((keys) => this.nameAll(keys))),
    { initialValue: new Map<string, string>() as ReadonlyMap<string, string> },
  );

  /** The card whose record the advisor is asked to confirm; one dialog at a time. */
  protected readonly confirming = signal<ActionCard | null>(null);

  /** While the thread is read, the create buttons wait, so nothing is created twice meanwhile. */
  protected readonly restoring = computed(() =>
    this.store.restoring(this.threadId()),
  );

  /** The thread could not be read, so nothing is offered: it may have been created already. */
  protected readonly unread = computed(() =>
    this.store.unread(this.threadId()),
  );

  constructor() {
    // What the thread kept is read when its cards are drawn: a record created in an earlier
    // session, or in another browser, shows as created rather than being offered again.
    effect(() => {
      const threadId = this.threadId();
      untracked(() => this.store.restore(threadId));
    });
    inject(DestroyRef).onDestroy(() => (this.destroyed = true));
    // A lost save means the record is offered again later, so how often that happens is tracked.
    this.store.saveFailures
      .pipe(takeUntilDestroyed())
      .subscribe((id) => this.trackLostSave(id));
  }

  protected statusOf(card: ActionCard): SuggestedActionStatus {
    return this.store.statusOf(card.id);
  }

  /** A created card shows the day its record was created for, and is never flagged as past. */
  protected dateOf(
    card: ActionCard,
    status: SuggestedActionStatus,
  ): { text: string; past: boolean } {
    if (status.state !== 'created') {
      return { text: card.date, past: card.past };
    }

    const { start, hasTime } = status.when ?? card.when;
    return {
      text: formatCitationDate(this.locale, start, hasTime),
      past: false,
    };
  }

  protected nameOf(card: ActionCard): string | undefined {
    const key = card.action.withEntityKey;
    return key ? this.names().get(key) : undefined;
  }

  protected confirm(card: ActionCard): void {
    this.confirming.set(this.refreshed(card));
  }

  protected cancel(card: ActionCard): void {
    this.confirming.set(null);
    this.focus(card, '.ai-action-create');
  }

  protected recheck(): void {
    this.store.restore(this.threadId());
  }

  // "Try again" asks for no second confirmation.
  protected retry(card: ActionCard): void {
    this.create(this.refreshed(card));
  }

  protected create(card: ActionCard): void {
    this.confirming.set(null);
    this.focus(card, '.ai-action-status');
    // The store keeps the request; this subscription only reports how the click ended.
    this.store.create(card.id, card.action, card.when).subscribe((key) => {
      // The card's own thread: the chat reuses this component when another conversation is shown.
      const { threadId } = card.id;
      this.tracker.trackEvent(
        EventJourney.RecommendedActions,
        EventAction.Clicked,
        {
          name: CREATE_EVENT_NAMES[card.action.type],
          result: key ? 'created' : 'failed',
          ...(threadId ? { thread_id: threadId } : {}),
        },
      );
      // Opened for the advisor to refine, as classic opens it, while its conversation is shown.
      if (key && !this.destroyed && this.threadId() === threadId) {
        this.openRecord(key);
      }
    });
  }

  protected open(card: ActionCard): void {
    const key = this.statusOf(card).key;
    if (key) {
      this.openRecord(key);
    }
  }

  private trackLostSave(id: SuggestedActionId): void {
    const card = this.cards().find(
      ({ id: { threadId, fence, index } }) =>
        threadId === id.threadId && fence === id.fence && index === id.index,
    );
    if (card) {
      this.tracker.trackEvent(
        EventJourney.RecommendedActions,
        EventAction.Saved,
        {
          name: CREATE_EVENT_NAMES[card.action.type],
          result: 'failed',
          ...(id.threadId ? { thread_id: id.threadId } : {}),
        },
      );
    }
  }

  private openRecord(key: string): void {
    this.service
      .open(key)
      .pipe(catchError(() => of(false)))
      .subscribe();
  }

  private toCard(
    id: SuggestedActionId,
    action: AnswerAction,
    now: Date,
  ): ActionCard {
    const when = resolveWhen(action, now);

    return {
      id,
      action,
      when,
      icon: crmRecordIcon(action.type),
      typeKey: crmCitationTypeKey(action.type),
      createKey: CREATE_LABEL_KEYS[action.type],
      date: formatCitationDate(this.locale, when.start, when.hasTime),
      past: when.start < (when.hasTime ? now : startOfDay(now)),
    };
  }

  private refreshed(card: ActionCard): ActionCard {
    return this.toCard(card.id, card.action, new Date());
  }

  // After the render that closes the dialog, or swaps the card's button for its status.
  private focus(card: ActionCard, selector: string): void {
    afterNextRender(
      () =>
        this.host.nativeElement
          .querySelector<HTMLElement>(
            `[data-index="${card.id.index}"] ${selector}`,
          )
          ?.focus(),
      { injector: this.injector },
    );
  }

  // An entry the CRM does not name, or cannot be asked for, leaves its card without a "for" line.
  private nameAll(keys: string[]): Observable<ReadonlyMap<string, string>> {
    if (keys.length === 0) {
      return of(new Map());
    }

    return forkJoin(
      keys.map((key) =>
        this.service.nameOf(key).pipe(catchError(() => of(null))),
      ),
    ).pipe(
      map(
        (names) =>
          new Map(
            keys.flatMap((key, i) => {
              const name = names[i];
              return name ? [[key, name] as const] : [];
            }),
          ),
      ),
    );
  }
}
